import type { Waypoint } from '@/store/mission';

/** Export waypoints as KML. */
export function toKML(name: string, waypoints: Waypoint[]): string {
  const coords = waypoints
    .map((w) => `${w.lon},${w.lat},${w.alt_m}`)
    .join(' ');
  const placemarks = waypoints
    .map(
      (w) => `
    <Placemark>
      <name>WP ${w.seq + 1}</name>
      <Point>
        <coordinates>${w.lon},${w.lat},${w.alt_m}</coordinates>
      </Point>
    </Placemark>`,
    )
    .join('');
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(name)}</name>
    <Placemark>
      <name>${escapeXml(name)} path</name>
      <LineString>
        <altitudeMode>relativeToGround</altitudeMode>
        <coordinates>${coords}</coordinates>
      </LineString>
    </Placemark>${placemarks}
  </Document>
</kml>
`;
}

/** Export waypoints as GeoJSON FeatureCollection. */
export function toGeoJSON(name: string, waypoints: Waypoint[]): string {
  const features = [
    {
      type: 'Feature' as const,
      properties: { name, kind: 'path' },
      geometry: {
        type: 'LineString' as const,
        coordinates: waypoints.map((w) => [w.lon, w.lat, w.alt_m]),
      },
    },
    ...waypoints.map((w) => ({
      type: 'Feature' as const,
      properties: { name: `WP ${w.seq + 1}`, ...w },
      geometry: {
        type: 'Point' as const,
        coordinates: [w.lon, w.lat, w.alt_m],
      },
    })),
  ];
  return JSON.stringify({ type: 'FeatureCollection', features }, null, 2);
}

/** Parse KML coordinates → waypoints. */
export function fromKML(xml: string, defaultAlt = 15): Waypoint[] {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  const lineStrings = doc.getElementsByTagName('coordinates');
  const wps: Waypoint[] = [];
  for (let i = 0; i < lineStrings.length; i++) {
    const text = lineStrings[i]?.textContent ?? '';
    const tokens = text.trim().split(/\s+/);
    for (const t of tokens) {
      const parts = t.split(',').map(Number);
      if (parts.length >= 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
        wps.push({
          seq: wps.length,
          lat: parts[1]!,
          lon: parts[0]!,
          alt_m: Number.isFinite(parts[2]) ? parts[2]! : defaultAlt,
        });
      }
    }
  }
  return wps;
}

/** Parse GeoJSON FeatureCollection → waypoints. */
export function fromGeoJSON(json: string, defaultAlt = 15): Waypoint[] {
  const parsed = JSON.parse(json);
  const wps: Waypoint[] = [];
  const features = parsed.features ?? [];
  for (const f of features) {
    const geom = f.geometry;
    if (!geom) continue;
    if (geom.type === 'Point') {
      const [lon, lat, alt] = geom.coordinates;
      wps.push({
        seq: wps.length,
        lat,
        lon,
        alt_m: typeof alt === 'number' ? alt : defaultAlt,
      });
    } else if (geom.type === 'LineString') {
      for (const c of geom.coordinates) {
        const [lon, lat, alt] = c;
        wps.push({
          seq: wps.length,
          lat,
          lon,
          alt_m: typeof alt === 'number' ? alt : defaultAlt,
        });
      }
    }
  }
  return wps;
}

function escapeXml(s: string): string {
  return s.replace(/[<>&'"]/g, (c) => {
    const m: Record<string, string> = { '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' };
    return m[c] ?? c;
  });
}

/** Trigger browser download of a string as a file. */
export function downloadText(filename: string, content: string, type = 'application/octet-stream') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
